/**
 * iter131 — Conversations Inbox (Bottom-nav tab).
 *
 * WhatsApp-style list of every guest conversation across every property
 * the owner manages. Sorted by last-message time. Tap → thread screen.
 * Polls every 15s while mounted so new inbound messages surface in
 * near real-time without a manual refresh.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, RefreshCw, Loader2, Circle } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { whatsappConversationAPI } from '../services/api';
import OtaLogo from '../components/OtaLogo';

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    const yesterday = new Date(); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

const ConversationRow = ({ convo, onOpen }) => {
  const unread = convo.unread_count || 0;
  const guest = convo.guest_name || convo.booking?.guest_name || 'Guest';
  const preview = convo.last_message_preview || '(no messages yet)';
  const when = formatWhen(convo.last_message_at || convo.updated_at);
  const ota = convo.ota_source || convo.booking?.ota_source || 'OTA';

  return (
    <button
      onClick={() => onOpen(convo.booking_id)}
      className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 border-b border-gray-100 transition-colors"
      data-testid={`inbox-row-${convo.booking_id}`}
    >
      <div className="relative flex-shrink-0">
        <div className="w-11 h-11 rounded-full bg-[#EEF1FC] flex items-center justify-center text-[#1B2A7B] font-semibold">
          {guest.charAt(0).toUpperCase()}
        </div>
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            data-testid={`inbox-row-unread-${convo.booking_id}`}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-gray-900 truncate" data-testid={`inbox-row-name-${convo.booking_id}`}>
            {guest}
          </div>
          <div className="text-xs text-gray-500 flex-shrink-0" data-testid={`inbox-row-when-${convo.booking_id}`}>
            {when}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <OtaLogo source={ota} className="w-4 h-4" />
          <span className="text-[11px] text-gray-500 truncate">{ota}</span>
          <span className="text-[11px] text-gray-400">•</span>
          <span className="text-[11px] text-gray-500 truncate">{convo.booking_id}</span>
        </div>
        <div
          className={`text-sm mt-1 truncate ${unread > 0 ? 'text-gray-900 font-medium' : 'text-gray-600'}`}
          data-testid={`inbox-row-preview-${convo.booking_id}`}
        >
          {convo.last_direction === 'outbound' && <span className="text-[#1B2A7B] mr-1">You:</span>}
          {preview}
        </div>
      </div>
    </button>
  );
};

const ConversationsInboxScreen = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    setError('');
    try {
      const res = await whatsappConversationAPI.inbox();
      const items = res?.RESULT?.conversations || [];
      setConversations(items);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load conversations';
      setError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 15s while visible
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') load({ silent: true });
    }, 15000);
    return () => clearInterval(iv);
  }, [load]);

  const handleOpen = (bookingId) => navigate(`/conversations/${bookingId}`);

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="conversations-inbox-screen">
      <AppHeader />
      <div className="max-w-lg mx-auto">
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#1B2A7B]" />
            <h1 className="text-lg font-bold text-gray-900" data-testid="conversations-title">Conversations</h1>
            {totalUnread > 0 && (
              <span
                className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5"
                data-testid="conversations-total-unread"
              >
                {totalUnread}
              </span>
            )}
          </div>
          <button
            onClick={() => load()}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
            data-testid="conversations-refresh-btn"
            disabled={refreshing || loading}
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading && (
          <div className="p-8 flex items-center justify-center text-gray-500" data-testid="conversations-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading conversations…
          </div>
        )}

        {!loading && error && (
          <div className="p-4 m-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="conversations-error">
            {error}
          </div>
        )}

        {!loading && !error && conversations.length === 0 && (
          <div className="p-10 text-center" data-testid="conversations-empty">
            <div className="w-14 h-14 rounded-full bg-gray-100 mx-auto flex items-center justify-center mb-3">
              <MessageSquare className="w-6 h-6 text-gray-400" />
            </div>
            <div className="text-gray-700 font-medium">No conversations yet</div>
            <div className="text-sm text-gray-500 mt-1">
              Guest WhatsApp messages will appear here once your Admin Panel starts syncing.
            </div>
          </div>
        )}

        {!loading && !error && conversations.length > 0 && (
          <div className="bg-white" data-testid="conversations-list">
            {conversations.map((c) => (
              <ConversationRow key={c.booking_id} convo={c} onOpen={handleOpen} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsInboxScreen;
